<template>
  <section
    class="SeasonRunListView"
  >
    <ol class="SeasonRunListView__monthList">
      <li
        v-for="month in months"
        :key="month.datetime"
        class="SeasonRunListView__monthItem"
        :class="{
          'SeasonRunListView__monthItem--isPast': month.isPast,
        }"
      >
        <h3 class="SeasonRunListView__monthHeader">
          {{ month.label }}
        </h3>
        <ol class="SeasonRunListView__runList">
          <li
            v-for="item in month.items"
            :key="item.run.id"
            class="SeasonRunListView__runItem"
            :class="{
              'SeasonRunListView__runItem--isPast': item.isPast,
              'SeasonRunListView__runItem--isCanceled': item.isCanceled,
              'SeasonRunListView__runItem--isDifferentDay': item.isDifferentDay,
              'SeasonRunListView__runItem--isDifferentTime': item.isDifferentTime,
            }"
            :data-testid="item.run.id"
          >
            <RouterLink
              class="SeasonRunListView__runLink"
              :to="getRouteForRun(item.run.id)"
              @click.prevent="openModal(getRouteForRun(item.run.id))"
            >
              <time
                class="SeasonRunListView__runTime"
                :datetime="item.run.start_at"
              >
                <span class="SeasonRunListView__runDay">{{ item.formattedDay }}</span>
                <span class="SeasonRunListView__runDate">, {{ item.formattedDate }}</span>
                <span> @ </span>
                <span class="SeasonRunListView__runTimeRange">{{ item.formattedTimeRange }}</span>
              </time>
            </RouterLink>
          </li>
        </ol>
      </li>
    </ol>
  </section>
</template>

<script lang="ts" setup>
import useNow from "@/composables/useNow";
import getRouteForRun from "@/lib/utils/route/getRouteForRun";
import useModalStore from "@/stores/useModalStore";
import { TZDate } from "@date-fns/tz";
import { Run, SeasonWithMaybeCourt } from "@shared/lib/database/query.types";
import formatRunTimeRange from "@shared/lib/utils/date/formatRunTimeRange";
import { endOfDay, endOfMonth, format, isAfter, parseISO, startOfMonth } from "date-fns";
import { computed } from "vue";

const {
  runs,
  season,
} = defineProps<{
  runs: Array<Run>,
  season?: SeasonWithMaybeCourt,
}>();

const {
  openModal,
} = useModalStore();

const {
  now,
} = useNow();

const sortedRuns = computed(() => {
  return [...runs].sort((a, b) => {
    return +new Date(a.start_at) - +new Date(b.start_at);
  });
});

const seasonStartDate = computed(() => {
  if (!season?.start_on) return;

  return parseISO(season.start_on);
});

const months = computed(() => {
  return sortedRuns.value.reduce<Array<{
    datetime: string,
    label: string,
    isPast: boolean,
    items: Array<{
      run: Run,
      formattedDay: string,
      formattedDate: string,
      formattedTimeRange: string,
      isPast: boolean,
      isCanceled: boolean,
      isDifferentDay: boolean,
      isDifferentTime: boolean,
      dayOfWeek: number,
    }>,
  }>>((acc, run) => {
    const startDateInTimezone = new TZDate(parseISO(run.start_at), season?.court?.timezone || "America/New_York");
    const endDateInTimezone = run.end_at ? new TZDate(parseISO(run.end_at), season?.court?.timezone || "America/New_York") : null;
    const month = startOfMonth(startDateInTimezone);
    const datetime = month.toISOString();
    const isPast = isAfter(now.value, endOfDay(startDateInTimezone));

    const item = {
      run,
      formattedDay: format(startDateInTimezone, "EEE"),
      formattedDate: format(startDateInTimezone, "MMM dd"),
      formattedTimeRange: formatRunTimeRange(run, season?.court?.timezone || "America/New_York"), // TODO: figure out fallback timezone
      isPast,
      isCanceled: !!run.canceled_at,
      isDifferentDay: isDifferentDay(parseISO(run.start_at.substring(0, 10))),
      isDifferentTime: isDifferentTime(startDateInTimezone, endDateInTimezone),
      dayOfWeek: startDateInTimezone.getDay(),
    };

    if (acc.length === 0 || acc.at(-1)?.datetime !== datetime) {
      acc.push({
        datetime,
        isPast: isAfter(now.value, endOfMonth(startDateInTimezone)),
        label: format(startDateInTimezone, "MMMM"),
        items: [item],
      });
    } else {
      acc.at(-1)?.items.push(item);
    }

    return acc;
  }, []);
});

function isDifferentDay (runDate: Date) {
  if (!seasonStartDate.value) return false;

  return runDate.getDay() !== seasonStartDate.value.getDay();
}

function isDifferentTime (runStartDate: Date, runEndDate: Date | null) {
  if (!season) return false;

  const runStartTime = format(runStartDate, "HH:mm:ss");
  const runEndTime = runEndDate ? format(runEndDate, "HH:mm:ss") : null;

  return runStartTime !== season.default_start_time || runEndTime !== season.default_end_time;
}
</script>

<style lang="scss" scoped>
.SeasonRunListView {
  --itemHeight: 48px;
  position: relative;

  display: grid;
  align-content: start;
  overflow: hidden;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: 8px;
  background-color: var(--themeColorWhite);
}

.SeasonRunListView__monthList {
  display: grid;
  gap: 2px;
}

.SeasonRunListView__monthItem {
  display: grid;
  gap: 2px;
}

.SeasonRunListView__monthHeader {
  position: relative;

  display: flex;
  align-items: center;
  height: 44px;
  padding: var(--fontSafariOffset) 16px 0;

  outline: solid 2px var(--themeColorNeutral900);
  background-color: var(--themeColorSecondary);
  color: var(--themeColorSecondaryForeground);

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  text-transform: uppercase;

  .SeasonRunListView__monthItem--isPast & {
    background-color: var(--themeColorNeutral200);
    color: var(--themeColorNeutral900);
  }
}

.SeasonRunListView__runItem {
  font-family: var(--fontFamilyMono);
  font-size: 18px;

  & + & {
    border-top: solid 1px var(--themeColorNeutral100);
  }
}

.SeasonRunListView__runItem--isPast {
  color: var(--themeColorNeutral400);
}

.SeasonRunListView__runLink {
  display: flex;
  align-items: center;
  min-height: var(--itemHeight);
  padding: var(--fontSafariOffset) 16px 0;
}

.SeasonRunListView__runTime {
  .SeasonRunListView__runItem--isCanceled & span {
    color: var(--themeColorNeutral400);

    text-decoration: line-through;
    text-decoration-color: currentColor;
    text-decoration-thickness: 2px;
  }
}

.SeasonRunListView__runItem:not(.SeasonRunListView__runItem--isPast) {
  &.SeasonRunListView__runItem--isDifferentDay .SeasonRunListView__runDay,
  &.SeasonRunListView__runItem--isDifferentTime .SeasonRunListView__runTimeRange {
    font-weight: var(--fontWeightMedium);
    text-decoration: underline;
    text-decoration-style: wavy;
    text-decoration-color: var(--themeColorSecondary);
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }
}
</style>
